<template>
	<div class="ks-data-table" :class="{ 'ks-data-table-dense': dense, 'ks-data-table-fill-height': fillHeight }">
		<div class="ks-data-table-scroller" :class="{ 'ks-data-table-scroller-fill-height': fillHeight }">
			<!-- Header -->
			<table class="ks-data-table-table">
				<thead v-if="!hideHeaders">
					<slot name="headers" :headers="computedHeaders" :all="allSelected" :indeterminate="indeterminate">
						<tr>
							<th
								v-for="header in computedHeaders"
								:key="header.value"
								:class="getHeaderClasses(header)"
								:style="getHeaderStyle(header)"
								@click="header.sortable !== false ? changeSort(header.value) : null"
							>
								<slot name="headerCell" :header="header">
									<span>{{ header.text }}</span>
									<span v-if="header.sortable !== false" class="ks-data-table-sort-icon material-icons" :class="{ 'ks-data-table-sort-active': internalPagination.sortBy === header.value, 'ks-data-table-sort-desc': internalPagination.descending }">arrow_upward</span>
								</slot>
							</th>
						</tr>
					</slot>
				</thead>
				<tbody>
					<template v-if="computedItems.length > 0">
						<slot name="items" v-for="(item, index) in paginatedItems" :item="item" :index="index" :selected="isSelected(item)" :select="(val) => select(item, val)">
							<tr :key="index">
								<td v-for="header in computedHeaders" :key="header.value">{{ getObjectValueByPath(item, header.value) }}</td>
							</tr>
						</slot>
					</template>
					<tr v-else-if="noDataText">
						<td :colspan="computedHeaders.length" class="ks-data-table-no-data">{{ noDataText }}</td>
					</tr>
				</tbody>
			</table>
		</div>
		<!-- Footer / Pagination -->
		<div v-if="!hideActions" class="ks-data-table-footer">
			<div class="ks-data-table-footer-info">
				<span>Rows per page:</span>
				<select v-model="internalRowsPerPage" class="ks-data-table-rows-select" @change="onRowsPerPageChange">
					<option v-for="(item, i) in computedRowsPerPageItems" :key="i" :value="item.value">{{ item.text }}</option>
				</select>
			</div>
			<div class="ks-data-table-footer-pagination">
				<span>{{ paginationInfo }}</span>
				<button class="ks-data-table-page-btn" :disabled="internalPagination.page <= 1" @click="prevPage">
					<span class="material-icons">chevron_left</span>
				</button>
				<button class="ks-data-table-page-btn" :disabled="internalPagination.page >= totalPages" @click="nextPage">
					<span class="material-icons">chevron_right</span>
				</button>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: "KDataTable",
	props: {
		value: {
			type: Array,
			default: () => []
		},
		headers: {
			type: Array,
			default: () => []
		},
		items: {
			type: Array,
			default: () => []
		},
		pagination: {
			type: Object,
			default: null
		},
		rowsPerPageItems: {
			type: Array,
			default: () => [5, 15, 25, 50, 100, { text: "All", value: -1 }]
		},
		search: {
			type: String,
			default: ""
		},
		hideHeaders: {
			type: Boolean,
			default: false
		},
		hideActions: {
			type: Boolean,
			default: false
		},
		disableInitialSort: {
			type: Boolean,
			default: false
		},
		noDataText: {
			type: String,
			default: "No data available"
		},
		dense: {
			type: Boolean,
			default: false
		},
		fillHeight: {
			type: Boolean,
			default: false
		},
		selectAll: {
			type: [Boolean, String],
			default: false
		},
		itemKey: {
			type: String,
			default: "id"
		}
	},
	data () {
		return {
			internalPagination: {
				page: 1,
				rowsPerPage: -1,
				sortBy: null,
				descending: false,
				totalItems: 0
			},
			internalRowsPerPage: -1,
			selectedItems: [...this.value]
		}
	},
	computed: {
		computedHeaders () {
			return this.headers
		},
		computedRowsPerPageItems () {
			return this.rowsPerPageItems.map(item => {
				if (typeof item === "object") return item
				return { text: String(item), value: item }
			})
		},
		filteredItems () {
			let items = [...this.items]
			if (this.search) {
				const s = this.search.toLowerCase()
				items = items.filter(item => {
					return Object.values(item).some(val => {
						if (val == null) return false
						if (typeof(val) == "object") {
							return Object.values(val).some(v => {
								if (v == null) return false;
								return String(v).toLowerCase().includes(s);
							})
						}
						return String(val).toLowerCase().includes(s)
					})
				})
			}
			return items
		},
		computedItems () {
			let items = this.filteredItems
			if (this.internalPagination.sortBy) {
				const sortBy = this.internalPagination.sortBy
				const desc = this.internalPagination.descending ? -1 : 1
				items = [...items].sort((a, b) => {
					let valA = this.getObjectValueByPath(a, sortBy)
					let valB = this.getObjectValueByPath(b, sortBy)
					if (valA == null) valA = ""
					if (valB == null) valB = ""
					if (!isNaN(valA) && !isNaN(valB)) {
						return (Number(valA) - Number(valB)) * desc
					}
					return String(valA).localeCompare(String(valB)) * desc
				})
			}
			return items
		},
		paginatedItems () {
			const rowsPerPage = this.internalPagination.rowsPerPage
			if (rowsPerPage === -1) return this.computedItems
			const start = (this.internalPagination.page - 1) * rowsPerPage
			return this.computedItems.slice(start, start + rowsPerPage)
		},
		totalPages () {
			const rowsPerPage = this.internalPagination.rowsPerPage
			if (rowsPerPage === -1) return 1
			return Math.ceil(this.computedItems.length / rowsPerPage) || 1
		},
		paginationInfo () {
			const total = this.computedItems.length
			if (total === 0) return "0 of 0"
			const rowsPerPage = this.internalPagination.rowsPerPage
			if (rowsPerPage === -1) return `1-${total} of ${total}`
			const start = (this.internalPagination.page - 1) * rowsPerPage + 1
			const end = Math.min(this.internalPagination.page * rowsPerPage, total)
			return `${start}-${end} of ${total}`
		},
		allSelected () {
			if (this.computedItems.length === 0) return false
			return this.selectedItems.length === this.computedItems.length
		},
		indeterminate () {
			return this.selectedItems.length > 0 && this.selectedItems.length < this.computedItems.length
		}
	},
	watch: {
		value (val) {
			const key = this.itemKey
			const isSame = val.length === this.selectedItems.length &&
				val.every((v, i) => v[key] === this.selectedItems[i][key])
			if (!isSame) {
				this.selectedItems = [...val]
			}
		},
		selectedItems (val) {
			this.$emit("input", val)
		},
		pagination: {
			handler (val) {
				if (val) {
					this.updateInternalPagination(val)
				}
			},
			deep: true,
			immediate: true
		},
		internalPagination: {
			handler (val) {
				this.$emit("update:pagination", { ...val, totalItems: this.computedItems.length })
			},
			deep: true
		},
		items () {
			this.internalPagination.totalItems = this.computedItems.length
			if (this.internalPagination.page > this.totalPages) {
				this.internalPagination.page = 1
			}
		},
		search () {
			this.internalPagination.page = 1
			this.internalPagination.totalItems = this.computedItems.length
		},
		computedItems (val) {
			this.internalPagination.totalItems = val.length
		}
	},
	created () {
		if (this.pagination) {
			this.updateInternalPagination(this.pagination)
		} else {
			const allOption = this.rowsPerPageItems.find(item => {
				if (typeof item === "object") return item.value === -1
				return item === -1
			})
			const defaultRpp = allOption !== undefined
				? (typeof allOption === "object" ? allOption.value : allOption)
				: this.rowsPerPageItems[0]
			this.internalPagination.rowsPerPage = typeof defaultRpp === "object" ? defaultRpp.value : defaultRpp
			this.internalRowsPerPage = this.internalPagination.rowsPerPage
		}
		this.internalPagination.totalItems = this.computedItems.length
	},
	methods: {
		updateInternalPagination (val) {
			if (val.sortBy !== undefined) this.internalPagination.sortBy = val.sortBy
			if (val.descending !== undefined) this.internalPagination.descending = val.descending
			if (val.page !== undefined) this.internalPagination.page = val.page
			if (val.rowsPerPage !== undefined) {
				this.internalPagination.rowsPerPage = val.rowsPerPage
				this.internalRowsPerPage = val.rowsPerPage
			}
		},
		getHeaderClasses (header) {
			const classes = []
			if (header.align) classes.push("text-" + header.align)
			if (header.sortable !== false) classes.push("ks-data-table-sortable")
			if (this.internalPagination.sortBy === header.value) classes.push("ks-data-table-sort-active")
			if (this.internalPagination.descending) classes.push("desc")
			else classes.push("asc")
			return classes
		},
		getHeaderStyle (header) {
			const style = {}
			if (header.width) style.width = typeof header.width === "number" ? header.width + "px" : header.width
			return style
		},
		changeSort (column) {
			if (this.internalPagination.sortBy === column) {
				this.internalPagination.descending = !this.internalPagination.descending
			} else {
				this.internalPagination.sortBy = column
				this.internalPagination.descending = false
			}
		},
		prevPage () {
			if (this.internalPagination.page > 1) {
				this.internalPagination.page--
			}
		},
		nextPage () {
			if (this.internalPagination.page < this.totalPages) {
				this.internalPagination.page++
			}
		},
		onRowsPerPageChange () {
			this.internalPagination.rowsPerPage = Number(this.internalRowsPerPage)
			this.internalPagination.page = 1
		},
		isSelected (item) {
			const key = this.itemKey
			return this.selectedItems.some(s => s[key] === item[key])
		},
		select (item, val) {
			const key = this.itemKey
			const index = this.selectedItems.findIndex(s => s[key] === item[key])
			if (val && index === -1) {
				this.selectedItems = [...this.selectedItems, item]
			} else if (!val && index > -1) {
				const copy = [...this.selectedItems]
				copy.splice(index, 1)
				this.selectedItems = copy
			}
		},
		toggleAll () {
			if (this.allSelected) {
				this.selectedItems = []
			} else {
				this.selectedItems = [...this.computedItems]
			}
		},
		getObjectValueByPath (obj, path) {
			if (!path) return obj
			const parts = path.split(".")
			let val = obj
			for (const part of parts) {
				if (val == null) return undefined
				val = val[part]
			}
			return val
		}
	}
}
</script>

<style>
.ks-data-table {
	width: 100%;
	background: transparent;
	overflow-x: auto;
}

.ks-data-table-fill-height {
	display: flex;
	flex-direction: column;
	flex: 1 1 auto;
	height: 100%;
	min-height: 0;
	overflow: hidden;
}

.ks-data-table-scroller {
	overflow-x: auto;
}

.ks-data-table-scroller-fill-height {
	flex: 1 1 auto;
	min-height: 0;
	overflow-y: auto;
}

.ks-data-table-fill-height .ks-data-table-table thead th {
	position: sticky;
	top: 0;
	z-index: var(--ks-z-sticky, 1);
	background: var(--ks-background-color, #111820);
}

.ks-data-table-table {
	width: 100%;
	border-collapse: collapse;
	border-spacing: 0;
}

.ks-data-table-table thead th {
	font-size: 12px;
	font-weight: 500;
	color: var(--ks-font-color-secondary, rgba(255, 255, 255, 0.7));
	text-align: left;
	padding: 6px 16px;
	border-bottom: 1px solid var(--ks-col-secondary, rgba(255, 255, 255, 0.12));
	user-select: none;
	white-space: nowrap;
}

.ks-data-table-sortable {
	cursor: pointer;
}

.ks-data-table-sortable:hover {
	color: var(--ks-font-color-primary, #fff);
}

.ks-data-table-sort-icon {
	font-size: 16px;
	vertical-align: middle;
	opacity: 0;
	transition: opacity 0.2s, transform 0.2s;
	margin-left: 4px;
}

.ks-data-table-sortable:hover .ks-data-table-sort-icon {
	opacity: 0.5;
}

.ks-data-table-sort-icon.ks-data-table-sort-active {
	opacity: 1;
	color: var(--ks-font-color-primary, #fff);
}

.ks-data-table-sort-icon.ks-data-table-sort-desc {
	transform: rotate(180deg);
}

.ks-data-table-table tbody td {
	font-size: 12px;
	color: var(--ks-font-color-primary, #e3eaf1);
	padding: 8px 16px;
	border-bottom: 1px solid var(--ks-col-secondary, rgba(255, 255, 255, 0.08));
}

.ks-data-table-table tbody tr:hover {
	background: var(--ks-col-secondary, rgba(255, 255, 255, 0.04));
}

.ks-data-table-dense .ks-data-table-table thead th {
	padding: 8px 16px;
}

.ks-data-table-dense .ks-data-table-table tbody td {
	padding: 6px 16px;
	font-size: 12px;
}

.ks-data-table-no-data {
	text-align: center;
	padding: 24px 16px;
	color: var(--ks-font-color-secondary, rgba(255, 255, 255, 0.5));
}

.ks-data-table-footer {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	padding: 8px 16px;
	font-size: 12px;
	color: var(--ks-font-color-secondary, rgba(255, 255, 255, 0.7));
	border-top: 1px solid var(--ks-col-secondary, rgba(255, 255, 255, 0.08));
}

.ks-data-table-footer-info {
	display: flex;
	align-items: center;
	margin-right: 24px;
}

.ks-data-table-rows-select {
	margin-left: 8px;
	padding: 4px 8px;
	background: transparent;
	color: var(--ks-font-color-primary, #e3eaf1);
	border: 1px solid var(--ks-col-secondary, rgba(255, 255, 255, 0.2));
	border-radius: 4px;
	outline: none;
	cursor: pointer;
}

.ks-data-table-rows-select option {
	background: var(--ks-background-color, #111820);
	color: var(--ks-font-color-primary, #e3eaf1);
}

.ks-data-table-footer-pagination {
	display: flex;
	align-items: center;
	gap: 8px;
}

.ks-data-table-page-btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	border: none;
	background: transparent;
	color: var(--ks-font-color-primary, #e3eaf1);
	border-radius: 50%;
	cursor: pointer;
	transition: background 0.2s;
}

.ks-data-table-page-btn:hover:not(:disabled) {
	background: var(--ks-col-secondary, rgba(255, 255, 255, 0.08));
}

.ks-data-table-page-btn:disabled {
	opacity: 0.3;
	cursor: default;
}

.text-xs-left { text-align: left; }
.text-xs-center { text-align: center; }
.text-xs-right { text-align: right; }

.ks-data-table .ks-btn {
	margin-top: 0px !important;
	margin-bottom: 0px !important;
	padding-top: 0px !important;
	padding-bottom: 0px !important;
	margin-right: 5px !important;
}
.ks-data-table .ks-checkbox-wrapper {
	margin-top: 0px !important;
	margin-bottom: 0px !important;
	padding-top: 0px !important;
	padding-bottom: 0px !important;
	height: 20px !important;

}
.ks-data-table .ks-btn-icon {
	height: 20px !important;
	width: 20px !important;
}

</style>
